import { inject, Injectable } from '@angular/core'
import { EditorAssistance } from '../../../models/EditorAssistance'
import { AlertController, ModalController, NavController } from '@ionic/angular'
import { AssistanceEditorModalService } from '../assistance-editor-modal/assistance-editor-modal.service'
import { AssistanceTemplateService } from '../../../services/domain/assistance-template.service'
import { AssistanceStatusesService } from '../../../services/domain/codelists/assistance-statuses.service'
import { firstValueFrom } from 'rxjs'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { FeathersService } from '../../../services/feathers.service'
import { WorkerSelectForModalComponent } from '../../worker/worker-select-for-modal/worker-select-for-modal.component'
import { IRecommendedAssistantsFilter } from './recommended-assistants-filter'
import { DateRange } from '../../../services/date-utils.service'
import { AssistanceEditorModalComponent } from '../assistance-editor-modal/assistance-editor-modal.component'
import { PrimeNgToastService } from '../../../services/prime-ng-toast/prime-ng-toast.service'

@Injectable({
  providedIn: 'root',
})
export class AssistanceEditorService {
  private alertCtrl = inject(AlertController)
  private modalCtrl = inject(ModalController)
  private navCtrl = inject(NavController)
  private modalService = inject(AssistanceEditorModalService)
  private assistanceTemplateService = inject(AssistanceTemplateService)
  private toaster = inject(PrimeNgToastService)
  private assistanceStatusService = inject(AssistanceStatusesService)
  private feathers = inject(FeathersService)

  async tryCreateTemplate(assistance: EditorAssistance) {
    const template = assistance.toTemplate()

    if (template && template.hasAllRequiredFields()) {
      const alert = await this.alertCtrl.create({
        header: 'Šablona byla vytvořena',
        message: 'Chcete přejít na nově vytvořenou šablonu?',
        buttons: [
          {
            text: 'Ne',
            role: 'cancel',
          },
          {
            text: 'Ano, přejít',
            handler: async () => {
              await this.modalService.closeModal(AssistanceEditorModalComponent)
              await this.assistanceTemplateService.create(template)
              await this.navCtrl.navigateRoot(
                '/menu/app/tabs/clients/client/' + template.clientId + '/assistance-templates',
              )
            },
          },
        ],
      })

      await alert.present()
    } else {
      this.toaster.displayCustomToast(
        'error',
        'Asistence nemá vyplněna všechna potřebná pole pro vytvoření šablony - ' +
          template.getMissingRequiredFields().join(', '),
      )
    }
  }

  async create(assistanceFromForm: Partial<EditorAssistance>, isSplitSeries = false): Promise<EditorAssistance> {
    // TODO: Mozna assistanceFromForm ma mit required RecurrenceVeci (implements Recurrable)
    const instance = EditorAssistance.createFromProps(EditorAssistance, assistanceFromForm)
    if (isSplitSeries) {
      return await this.feathers.customMethodWithFactory(EditorAssistance, instance, (service, data) =>
        service.splitSeries(data),
      )
    } else {
      return await this.feathers.createWithFactory(EditorAssistance, instance)
    }
  }

  async remove(assistance: EditorAssistance) {
    return await this.feathers.removeByUrid(EditorAssistance, assistance.URID)
  }

  async patch(props: Partial<EditorAssistance> & { urid: string }): Promise<EditorAssistance> {
    return await this.feathers.patchByUrid(EditorAssistance, props)
  }

  async canDeleteAssistance(statusId: number) {
    const allowedIds = (
      await firstValueFrom(this.assistanceStatusService.getByNames([AssistanceStatusName.PLANNED]))
    ).map((status) => status.id)
    return allowedIds.includes(statusId)
  }

  async openWorkerSelect(
    URID: string,
    assistanceRange: DateRange,
    dismissCallback: (data: { changes: boolean; assistanceURID: string }) => void,
    filterOverrides: Partial<IRecommendedAssistantsFilter> = {},
  ) {
    // console.log(this.TAG, 'openWorkerSelect with', assistance)

    const modal = await this.modalCtrl.create({
      component: WorkerSelectForModalComponent,
      cssClass: 'workerSelect',
      componentProps: {
        assistanceURID: URID,
        assistanceRange,
        filterOverrides,
      },
    })

    modal.onDidDismiss().then((data) => {
      console.log('worker select dismiss', data.data.changes)
      dismissCallback(data.data)
    })

    return await modal.present()
  }
}
